import { useParams, useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { AnimatePresence } from "framer-motion";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import { constructSupportChatHref, routePaths } from "../../../common/infrastructure/routes/routePaths";
import { useHistoryBack } from "../../../common/infrastructure/hooks/useHistoryBack";
import { useApiMutation } from "../../../common/infrastructure/hooks/useApiMutation";
import { SupportChatCategory, SupportChatPriority, SupportChatStatus } from "../../../common/enums";
import {
  supportChatCategoryLabels,
  supportChatCategoryOptions,
  supportChatPriorityLabels,
} from "../models/supportChatModels";
import { Icon, IconType } from "../../../common/icons/Icon";
import { DefaultInputField } from "../../../common/components/forms/inputs/InputField/DefaultInputField";
import { DefaultFormSelectField } from "../../../common/components/forms/inputs/dropdown/DefaultFormSelectField";
import { DefaultTextareaField } from "../../../common/components/forms/inputs/textareaField/DefaultTextareaField";
import { SupportChatMessage } from "./SupportChatMessage";
import { SupportChatInput } from "./SupportChatInput";
import { CategoryChip, PriorityChip, StatusChip } from "../list/SupportChatItem";
import { FloatingPanel } from "../../../common/components/common/FloatingPanel/FloatingPanel";
import { useSupportChat } from "./hooks/useSupportChat";
import styles from "./SupportChat.module.scss";

interface ISupportChatProps {
  isNew?: boolean;
}

const newChatSchema = yup.object({
  title: yup.string().required("Заглавието е задължително.").max(300),
  category: yup.number().required(),
  priority: yup.number().required(),
  firstMessageContent: yup.string().required("Съобщението е задължително."),
});

type INewChatForm = yup.InferType<typeof newChatSchema>;

const categoryOptions = supportChatCategoryOptions.map((v) => ({
  id: v,
  name: supportChatCategoryLabels[v],
}));

const priorityOptions = Object.values(SupportChatPriority)
  .filter((v) => typeof v === "number")
  .map((v) => ({
    id: v as SupportChatPriority,
    name: supportChatPriorityLabels[v as SupportChatPriority],
  }));

export const SupportChat = ({ isNew = false }: ISupportChatProps) => {
  const { id } = useParams<{ id: string }>();
  const goBack = useHistoryBack(routePaths.supportTickets);

  const {
    chat,
    isLoading,
    groupedMessages,
    handleSend,
    handleCloseChat,
    handleReopenChat,
    isSending,
    isClosing,
    isReopening,
    messagesContainerRef,
    isGlobalAdmin,
    currentUser,
  } = useSupportChat(id);

  if (isNew) {
    return <NewChatForm />;
  }

  if (isLoading && !chat) {
    return (
      <FloatingPanel variant="glass" className={styles.chat_page}>
        <div className={styles.chat_header}>
          <div className={styles.chat_header_left}>
            <div className={`${styles.skeleton_block} ${styles.skeleton_back_btn}`} />
            <div>
              <div className={`${styles.skeleton_block} ${styles.skeleton_title}`} />
              <div className={styles.skeleton_meta}>
                <div className={`${styles.skeleton_block} ${styles.skeleton_chip}`} />
                <div className={`${styles.skeleton_block} ${styles.skeleton_chip}`} />
              </div>
            </div>
          </div>
        </div>
        <div className={`${styles.messages_container} ${styles.messages_container_skeleton}`}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={`${styles.skeleton_message} ${i % 2 === 0 ? styles.skeleton_message_own : ""}`}>
              {i % 2 !== 0 && <div className={`${styles.skeleton_block} ${styles.skeleton_avatar}`} />}
              <div className={`${styles.skeleton_block} ${styles.skeleton_bubble} ${i % 2 === 0 ? styles.skeleton_bubble_own : ""}`} />
            </div>
          ))}
        </div>
        <div className={styles.input_area}>
          <div className={`${styles.skeleton_block} ${styles.skeleton_input}`} />
        </div>
      </FloatingPanel>
    );
  }

  if (!chat) return null;

  return (
    <FloatingPanel variant="glass" className={styles.chat_page}>
      <div className={styles.chat_header}>
        <div className={styles.chat_header_left}>
          <button className={styles.chat_back_btn} onClick={goBack} type="button" aria-label="Назад">
            <Icon type={IconType.RightChevronIcon} width="20px" height="20px" />
          </button>
          <div className={styles.chat_header_info}>
            <div className={styles.chat_header_title} title={chat.title}>{chat.title}</div>
            <div className={styles.chat_header_meta}>
              <StatusChip status={chat.status} />
              <CategoryChip category={chat.category} />
              <PriorityChip priority={chat.priority} />
            </div>
          </div>
        </div>
        <div className={styles.chat_header_actions}>
          {isGlobalAdmin && chat.status === SupportChatStatus.Open && (
            <button className={styles.chat_action_btn} onClick={handleCloseChat} disabled={isClosing} type="button">
              {isClosing && <span className={styles.btn_spinner} />}
              Затвори
            </button>
          )}
          {isGlobalAdmin && chat.status === SupportChatStatus.Closed && (
            <button className={`${styles.chat_action_btn} ${styles.chat_action_btn_reopen}`} onClick={handleReopenChat} disabled={isReopening} type="button">
              {isReopening && <span className={styles.btn_spinner} />}
              Отвори отново
            </button>
          )}
        </div>
      </div>

      <div className={styles.messages_container} ref={messagesContainerRef}>
        <div className={styles.messages_inner}>
          <AnimatePresence initial={false}>
            {groupedMessages.map(({ msg, isGroupedWithPrev, isGroupedWithNext }) => (
              <SupportChatMessage
                key={msg.id}
                message={msg}
                isCurrentUser={msg.senderId === currentUser?.id}
                isGrouped={isGroupedWithPrev}
                hideAvatar={isGroupedWithNext}
                showTimestamp={!isGroupedWithNext}
                showTail={!isGroupedWithNext}
              />
            ))}
          </AnimatePresence>
        </div>
      </div>

      {chat.status === SupportChatStatus.Open && (
        <SupportChatInput chatId={chat.id} onSend={handleSend} isSending={isSending} />
      )}
      {chat.status === SupportChatStatus.Closed && (
        <div className={styles.chat_closed_notice}>
          Този чат е затворен.
          {isGlobalAdmin && (
            <button className={styles.chat_action_btn_inline} onClick={handleReopenChat} type="button">
              Отвори отново
            </button>
          )}
        </div>
      )}
    </FloatingPanel>
  );
};

// ---- New Chat Form ----

const NewChatForm = () => {
  const navigate = useNavigate();
  const goBack = useHistoryBack(routePaths.supportTickets);
  const form = useForm<INewChatForm>({
    resolver: yupResolver(newChatSchema),
    defaultValues: {
      title: "",
      category: SupportChatCategory.Technical,
      priority: SupportChatPriority.Medium,
      firstMessageContent: "",
    },
  });

  const { mutate: createChat, isLoading } = useApiMutation<string>({
    onSuccess: (data) => {
      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      const newId = parsed?.id ?? parsed?.Id;
      navigate(constructSupportChatHref(newId), { replace: true });
    },
  });

  const onSubmit = (values: INewChatForm) => {
    createChat({
      endpoint: endpointsMetadata.supportChat.create,
      method: "post",
      payload: values,
      disableSuccessToast: true,
    });
  };

  return (
    <FloatingPanel variant="glass" className={styles.new_chat_page}>
      <div className={styles.new_chat_header}>
        <h2 className={styles.new_chat_title}>Ново запитване</h2>
        <p className={styles.new_chat_subtitle}>
          Опишете проблема или въпроса си и ще Ви отговорим възможно най-скоро.
        </p>
      </div>

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className={styles.new_chat_form}>
          <DefaultInputField name="title" label="Заглавие" placeholder="Накратко опишете темата..." required width="100%" />
          <DefaultFormSelectField name="category" label="Категория" options={categoryOptions} required includeId={false} width="100%" />
          <DefaultFormSelectField name="priority" label="Приоритет" options={priorityOptions} required includeId={false} width="100%" />
          <DefaultTextareaField name="firstMessageContent" label="Съобщение" placeholder="Опишете подробно вашия въпрос или проблем..." rows={6} required width="100%" />
          <div className={styles.new_chat_actions}>
            <button type="button" className={styles.new_chat_cancel_btn} onClick={goBack}>
              Откажи
            </button>
            <button type="submit" className={styles.new_chat_submit_btn} disabled={isLoading}>
              <Icon type={IconType.SendIcon} width="16px" height="16px" />
              Изпрати запитване
            </button>
          </div>
        </form>
      </FormProvider>
    </FloatingPanel>
  );
};
